import React, { ReactNode, useState, useCallback } from "react";
import { StyleSheet, View, Platform } from "react-native";
import { useAuth } from "@/hooks/auth";
import { brand, colors, layout } from "@/constants";
import {
  AppTitle,
  Logo,
  NotificationCard,
  ScrollView,
  Spinner,
  TextButton,
  TextInput,
  ScreenLayout,
  Text
} from "@/components";

/** Wraps children in a <form> on web to suppress "[DOM] Password field is not contained in a form" and enable Enter-to-submit */
function FormWrapper({
  onSubmit,
  children
}: {
  onSubmit: () => void;
  children: ReactNode;
}) {
  if (Platform.OS !== "web") return <>{children}</>;
  return (
    <form
      onSubmit={(e: React.FormEvent) => {
        e.preventDefault();
        onSubmit();
      }}
      style={{ display: "contents" }}
    >
      {children}
    </form>
  );
}

/** Views within the login screen — no separate routes */
type LoginView = "signIn" | "createAccount" | "forgotPassword" | "verifyEmail";

/** Shared layout shell for all login views */
function LoginShell({ children }: { children: ReactNode }) {
  return (
    <ScreenLayout>
      <ScrollView contentContainerStyle={styles.scrollContent}>
        <Logo width={72} height={72} style={styles.logo} />
        <AppTitle style={styles.appTitle} />
        {children}
      </ScrollView>
    </ScreenLayout>
  );
}

/**
 * Login screen component that provides Google Sign-in and email/password
 * authentication, with waitlist confirmation for non-allowlisted users
 */
export default function LoginScreen() {
  const {
    signInWithGoogle,
    signUpWithEmail,
    signInWithEmail,
    resetPassword,
    resendVerificationEmail,
    clearEmailVerificationPending,
    clearError,
    isLoading,
    error,
    waitlistedEmail,
    emailVerificationPending
  } = useAuth();

  const [view, setView] = useState<LoginView>("signIn");
  // Email is intentionally preserved across view switches for convenience
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);
  const [resetEmailSentTo, setResetEmailSentTo] = useState<string | null>(null);

  // Derive the active view — emailVerificationPending from hook takes priority
  const activeView: LoginView = emailVerificationPending ? "verifyEmail" : view;

  const switchView = useCallback(
    (nextView: LoginView) => {
      if (isLoading) return;
      setView(nextView);
      setLocalError(null);
      clearError();
      setPassword("");
      setConfirmPassword("");
      setResetEmailSentTo(null);
    },
    [isLoading, clearError]
  );

  const handleSignIn = useCallback(async () => {
    setLocalError(null);
    if (!email.trim() || !password) {
      setLocalError("Please enter your email and password.");
      return;
    }
    await signInWithEmail(email.trim(), password);
  }, [email, password, signInWithEmail]);

  const handleCreateAccount = useCallback(async () => {
    setLocalError(null);
    if (!email.trim() || !password) {
      setLocalError("Please fill in all fields.");
      return;
    }
    if (password.length < 8) {
      setLocalError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirmPassword) {
      setLocalError("Passwords don't match.");
      return;
    }
    await signUpWithEmail(email.trim(), password);
  }, [email, password, confirmPassword, signUpWithEmail]);

  const handleResetPassword = useCallback(async () => {
    setLocalError(null);
    if (!email.trim()) {
      setLocalError("Please enter your email address.");
      return;
    }
    const success = await resetPassword(email.trim());
    if (success) setResetEmailSentTo(email.trim());
  }, [email, resetPassword]);

  const handleResendVerification = useCallback(async () => {
    if (!emailVerificationPending || !password) {
      setLocalError("Please sign in and try again.");
      return;
    }
    await resendVerificationEmail(emailVerificationPending, password);
  }, [emailVerificationPending, password, resendVerificationEmail]);

  const displayError = localError || error;

  // ── Waitlist view ──
  if (waitlistedEmail) {
    return (
      <LoginShell>
        <Text style={styles.subtitle} color="secondary">
          {brand.name} is available by invitation only.{"\n"}
          Sign in to access your account or join the waitlist.
        </Text>
        <View style={styles.cardContainer}>
          <NotificationCard icon="check_circle">
            <Text
              variant="regular"
              size="small"
              color="primary"
              style={styles.cardText}
            >
              {"You're on the waitlist! We'll let you know at "}
              <Text variant="medium" size="small" color="primary">
                {waitlistedEmail}
              </Text>
              {" when your spot is ready."}
            </Text>
          </NotificationCard>
        </View>
      </LoginShell>
    );
  }

  // ── Verify email view ──
  if (activeView === "verifyEmail") {
    return (
      <LoginShell>
        <View style={styles.cardContainer}>
          <NotificationCard icon="mail">
            <Text
              variant="regular"
              size="small"
              color="primary"
              style={styles.cardText}
            >
              {"We've sent a verification link to "}
              <Text variant="medium" size="small" color="primary">
                {emailVerificationPending}
              </Text>
              {". Verify your email, then sign in."}
            </Text>
          </NotificationCard>
        </View>
        {displayError && (
          <View style={styles.cardContainer}>
            <NotificationCard
              variant="error"
              label="Error"
              message={displayError}
            />
          </View>
        )}
        <View style={styles.buttonRow}>
          <TextButton
            buttonText="Sign In"
            onPress={() => {
              clearEmailVerificationPending();
              clearError();
              setLocalError(null);
              setPassword("");
              setView("signIn");
            }}
            disabled={isLoading}
          />
        </View>
        {isLoading && <Spinner size="small" style={styles.spinner} />}
        <Text
          style={styles.link}
          color="secondary"
          onPress={handleResendVerification}
        >
          {isLoading ? "Sending..." : "Resend verification email"}
        </Text>
      </LoginShell>
    );
  }

  // ── Forgot password view ──
  if (activeView === "forgotPassword") {
    return (
      <LoginShell>
        <View style={styles.formContainer}>
          <TextInput
            placeholder="Email"
            keyboardType="email-address"
            autoCapitalize="none"
            autoComplete="email"
            value={email}
            onChangeText={setEmail}
            editable={!isLoading}
          />
        </View>
        {resetEmailSentTo && (
          <View style={styles.cardContainer}>
            <NotificationCard icon="check_circle">
              <Text
                variant="regular"
                size="small"
                color="primary"
                style={styles.cardText}
              >
                {"Reset email sent to "}
                <Text variant="medium" size="small" color="primary">
                  {resetEmailSentTo}
                </Text>
                {"."}
              </Text>
            </NotificationCard>
          </View>
        )}
        {displayError && (
          <View style={styles.cardContainer}>
            <NotificationCard
              variant="error"
              label="Error"
              message={displayError}
            />
          </View>
        )}
        {!resetEmailSentTo && (
          <>
            <View style={styles.buttonRow}>
              <TextButton
                buttonText={isLoading ? "Sending..." : "Send Reset Email"}
                onPress={handleResetPassword}
                disabled={isLoading}
              />
            </View>
            {isLoading && <Spinner size="small" style={styles.spinner} />}
          </>
        )}
        <Text
          style={styles.link}
          color="secondary"
          onPress={() => switchView("signIn")}
        >
          Back to Sign In
        </Text>
        {resetEmailSentTo && (
          <Text
            style={styles.link}
            color="secondary"
            onPress={handleResetPassword}
          >
            {isLoading ? "Sending..." : "Resend reset email"}
          </Text>
        )}
      </LoginShell>
    );
  }

  // ── Create account view ──
  if (activeView === "createAccount") {
    return (
      <LoginShell>
        <FormWrapper onSubmit={handleCreateAccount}>
          <View style={styles.formContainer}>
            <TextInput
              placeholder="Email"
              keyboardType="email-address"
              autoCapitalize="none"
              autoComplete="email"
              value={email}
              onChangeText={setEmail}
              editable={!isLoading}
            />
            <TextInput
              placeholder="Password"
              secureTextEntry
              value={password}
              onChangeText={setPassword}
              editable={!isLoading}
              autoCapitalize="none"
            />
            <TextInput
              placeholder="Confirm Password"
              secureTextEntry
              value={confirmPassword}
              onChangeText={setConfirmPassword}
              editable={!isLoading}
              autoCapitalize="none"
            />
          </View>
          {displayError && (
            <View style={styles.cardContainer}>
              <NotificationCard
                variant="error"
                label="Error"
                message={displayError}
              />
            </View>
          )}
          <View style={styles.buttonRow}>
            <TextButton
              buttonText={isLoading ? "Creating..." : "Create Account"}
              onPress={handleCreateAccount}
              disabled={isLoading}
            />
          </View>
        </FormWrapper>
        {isLoading && <Spinner size="small" style={styles.spinner} />}
        <Text
          style={styles.link}
          color="secondary"
          onPress={() => switchView("signIn")}
        >
          Already have an account? Sign in
        </Text>
      </LoginShell>
    );
  }

  // ── Sign in view (default) ──
  return (
    <LoginShell>
      <Text style={styles.subtitle} color="secondary">
        SerenitAI is available by invitation only.{"\n"}
        Sign in to access your account or join the waitlist.
      </Text>

      <View style={styles.buttonContainer}>
        <TextButton
          buttonText="Sign in with Google"
          onPress={signInWithGoogle}
          disabled={isLoading}
        />
      </View>

      <View style={styles.dividerRow}>
        <View style={styles.dividerLine} />
        <Text
          variant="regular"
          size="small"
          color="tertiary"
          style={styles.dividerText}
        >
          or
        </Text>
        <View style={styles.dividerLine} />
      </View>

      <FormWrapper onSubmit={handleSignIn}>
        <View style={styles.formContainer}>
          <TextInput
            placeholder="Email"
            keyboardType="email-address"
            autoCapitalize="none"
            autoComplete="email"
            value={email}
            onChangeText={setEmail}
            editable={!isLoading}
          />
          <TextInput
            placeholder="Password"
            secureTextEntry
            value={password}
            onChangeText={setPassword}
            editable={!isLoading}
            autoCapitalize="none"
          />
        </View>

        {displayError && (
          <View style={styles.cardContainer}>
            <NotificationCard
              variant="error"
              label="Error"
              message={displayError}
            />
          </View>
        )}

        <View style={styles.buttonRow}>
          <TextButton
            buttonText={isLoading ? "Signing in..." : "Sign In"}
            onPress={handleSignIn}
            disabled={isLoading}
            variant="secondary"
          />
        </View>
      </FormWrapper>
      {isLoading && <Spinner size="small" style={styles.spinner} />}

      <Text
        style={styles.link}
        color="secondary"
        onPress={() => switchView("forgotPassword")}
      >
        Forgot password?
      </Text>
      <Text
        style={styles.link}
        color="secondary"
        onPress={() => switchView("createAccount")}
      >
        {"Don't have an account? Create one"}
      </Text>
    </LoginShell>
  );
}

const styles = StyleSheet.create({
  scrollContent: {
    flexGrow: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingTop: layout.space.xl,
    paddingBottom: layout.space.lg
  },
  logo: {
    marginBottom: layout.space.sm
  },
  appTitle: {
    marginBottom: layout.space.xl * 2
  },
  subtitle: {
    textAlign: "center",
    lineHeight: layout.size.lg,
    marginBottom: layout.space.xl * 2,
    paddingHorizontal: layout.size.lg
  },
  buttonContainer: {
    alignItems: "center",
    marginBottom: layout.space.md
  },
  dividerRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: layout.space.md,
    paddingHorizontal: layout.space.xl * 2,
    width: "100%",
    maxWidth: layout.minWidth
  },
  dividerLine: {
    flex: 1,
    height: layout.borderWidth.thin,
    backgroundColor: colors.outline
  },
  dividerText: {
    marginHorizontal: layout.space.md
  },
  formContainer: {
    width: "100%",
    maxWidth: layout.minWidth,
    paddingHorizontal: layout.space.lg,
    gap: layout.space.sm
  },
  buttonRow: {
    alignItems: "center",
    marginTop: layout.space.md
  },
  spinner: {
    marginTop: layout.space.xs
  },
  link: {
    marginTop: layout.space.md,
    textDecorationLine: "underline"
  },
  cardContainer: {
    width: "100%",
    paddingHorizontal: layout.space.lg,
    marginTop: layout.space.md
  },
  cardText: {
    flex: 1
  }
});
